import React, { useEffect, useState } from "react";
import moment from 'moment';
import { disableBack4Week, disableLastYear, disableNext4Week, getCurrentFirstDate, getFirstDateBack4Weeks, getFirstDateFromPickedDate, getFirstDateLastYear, getFirstDateNext4Weeks } from "../services/calendarService";
import { useMyContext } from "../context/Context";
import styles from "./../styles/datePicker.module.scss";
import { ReactComponent as Arrow } from "../assets/arrow.svg";
import PropTypes from "prop-types";

/**
 * @typedef {Object} DayProps
 * @property {boolean} isCurrentMonth
 * @property {boolean} isSelected
 * @property {boolean} isOutOfRange
 * @property {boolean} disabled
 */

const DatePicker = ({ handleClose }) => {
  const context = useMyContext();
  const [currentMonth, setCurrentMonth] = useState(context.firstDate);
  const [selectedDate, setSelectedDate] = useState(context.firstDate);
  const dayNames = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

  const onDateClick = date => {
    /* istanbul ignore else */
    if (
      date &&
      Object.prototype.toString.call(date) === "[object Date]" &&
      !isNaN(date.getTime()) &&
      date <= context.calEndDate &&
      date >= context.calStartDate
    ) {
      setSelectedDate(date);
      const newDate = getFirstDateFromPickedDate(date, context.calStartDate, context.calEndDate);
      context.firstDateChangeCallback(newDate);
      handleClose();
    }
  };

  const handleMonthChange = type => {
    /* istanbul ignore else */
    if (type === "next-month" && currentMonth <= context.calEndDate)
      setCurrentMonth(moment(currentMonth).add(1, "months"));
    /* istanbul ignore next */
    else if (type === "previous-month" && currentMonth >= context.calStartDate)
      setCurrentMonth(moment(currentMonth).subtract(1, "months"));
  };

  const onClickBack = () => {
    const newDate = getFirstDateBack4Weeks(context.firstDate, context.calStartDate);
    context.firstDateChangeCallback(newDate);
  };

  const onClickCurrent = () => {
    const newDate = getCurrentFirstDate();
    context.firstDateChangeCallback(newDate);
  };

  const onClickForward = () => {
    const newDate = getFirstDateNext4Weeks(context.firstDate, context.calEndDate);
    context.firstDateChangeCallback(newDate);
  };

  const onClickLastYear = () => {
    const newDate = getFirstDateLastYear(context.firstDate, context.calStartDate);
    context.firstDateChangeCallback(newDate);
  };

  useEffect(() => {
    setCurrentMonth(context.firstDate);
    setSelectedDate(context.firstDate);
  }, [context.firstDate]);

  const renderDatePickerHeader = () => {
    const dateFormat = "MMMM YYYY";

    return (
      <>
        <div className={styles.calendarHeader}>
          <div className={styles.headerText} data-testid="month-year-val">{moment(currentMonth).format(dateFormat)}</div>
          <div className={styles.iconWrapper}>
            <Arrow className={`${/* istanbul ignore next */ currentMonth >= context.calStartDate ? '' : styles.dim}`} onClick={() => handleMonthChange("previous-month")} data-testid="left-arrow-btn" />
            <Arrow className={`${styles.arrowRight} ${/* istanbul ignore next */ currentMonth <= context.calEndDate ? '' : styles.dim}`} onClick={() => handleMonthChange("next-month")} data-testid="right-arrow-btn" />
          </div>
        </div>
        <div className={styles.divider}></div>
        <div className={styles.dayNamesWrap}>
          {dayNames.map(dayName => <div key={dayName}>{dayName}</div>)}
        </div>
      </>
    );
  };

  const renderDatePickerBody = () => {
    const monthStart = moment(currentMonth).startOf('month').toDate();
    const monthEnd = moment(monthStart).endOf('month').toDate();
    const startDate = moment(monthStart).startOf('week').toDate();
    const endDate = moment(monthEnd).endOf('week').toDate();

    const dateFormat = "D";
    const rows = [];
    let day = startDate;

    const getDayProps = cloneDay => {
      const isCurrentMonth = moment(cloneDay).isSame(monthStart, "month");
      const isSelected = moment(cloneDay).isSame(selectedDate, "day");
      const isOutOfRange = cloneDay <= context.calStartDate || cloneDay >= context.calEndDate;
      const disabled = !isCurrentMonth || isOutOfRange;
      return { isCurrentMonth, isSelected, isOutOfRange, disabled };
    };

    const getButtonClass = ({ isCurrentMonth, isSelected, isOutOfRange, disabled }) => [
      styles.col,
      disabled ? styles.disabled : "",
      isSelected ? styles.selected : "",
      /* istanbul ignore next */ isCurrentMonth && isOutOfRange ? styles.dim : ""
    ].join(" ");

    const getSpanClass = ({ isCurrentMonth, isOutOfRange }) =>
      `${styles.number} ${!isCurrentMonth && isOutOfRange ? styles.disabledColor : ""}`;

    while (day <= endDate) {
      const days = [];
      for (let i = 0; i < 7; i++) {
        const cloneDay = new Date(day);
        const dayProps = getDayProps(cloneDay);

        days.push(
          <button
            type="button"
            className={getButtonClass(dayProps)}
            key={cloneDay.getTime()}
            onClick={() => !dayProps.disabled && onDateClick(cloneDay)}
            disabled={dayProps.disabled}
            aria-label={`Select date ${moment(cloneDay).format('MMMM D, YYYY')}`}
            aria-pressed={dayProps.isSelected}
          >
            <span className={getSpanClass(dayProps)}>
              {moment(cloneDay).format(dateFormat)}
            </span>
          </button>
        );
        day = moment(day).add(1, "days").toDate();
      }
      rows.push(
        <div className={styles.row} key={day.getTime()}>
          {days}
        </div>
      );
    }
    return <div className={styles.body}>{rows}</div>;
  };

  return (
    <div className={styles.calendar}>
      {renderDatePickerHeader()}
      {renderDatePickerBody()}
      <div className={styles.bottomWrap}>
        <button onClick={onClickCurrent} data-testid="this-month-btn">Current 4 Weeks</button>
        <button onClick={onClickForward} disabled={disableNext4Week(context.firstDate, context.calEndDate)} data-testid="next-month-btn">Next 4 Weeks</button>
        <button onClick={onClickBack} disabled={disableBack4Week(context.firstDate, context.calStartDate)} data-testid="last-month-btn">Back 4 Weeks</button>
        <button onClick={onClickLastYear} disabled={disableLastYear(context.firstDate, context.calStartDate)} data-testid="last-year-btn">LY Same Week</button>
      </div>
    </div>
  );
};

DatePicker.propTypes = {
  handleClose: PropTypes.func.isRequired
};

export default DatePicker;